import { HttpClient } from '@angular/common/http';
import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { OrganizationSettingsService } from 'app/core/http/services/organization-settings';
import { PaymentSettingsService } from 'app/core/http/services/payment-settings.service';
import { PaymentSettings } from 'app/core/interfaces/payment-settings';
import { buildRecipientVisualStatus, RecipientVisualStatus } from 'app/core/utils/recipient-status';
import { cnpjValidator } from 'app/core/validators/CnpjValidator';
import { cpfValidator } from 'app/core/validators/CpfValidator';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { OnboardingPagarmeComponent } from 'app/modules/shared/components/onboarding-pagarme/onboarding-pagarme.component';
import { DialogService } from 'app/services/dialog.service';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import { getHttpErrorMessage } from 'app/core/utils/http-error-message';

interface BankOption {
  ispb: string;
  name: string;
  code: number;
  fullName: string;
}

type CommissionSavePayload = Pick<PaymentSettings, 'organization_id' | 'organization_percentage'>;

@Component({
  selector: 'organization-bank-account',
  templateUrl: './organization-bank-account.component.html',
  styleUrls: ['./organization-bank-account.component.scss'],
})
export class OrganizationBankAccountComponent extends BaseComponent implements OnInit, OnDestroy {
  public bankDetailsForm: FormGroup;
  public paymentMethodsForm: FormGroup;
  public mode = 'CREATE';
  public paymentSettings!: PaymentSettings;
  public commissionPaymentSettings!: PaymentSettings;
  public commissionForm: FormGroup;
  public documentMask: string | null = null;
  public documentPlaceholder = 'Selecione o tipo de pessoa';
  public banks: BankOption[] = [];
  public filteredBanks$!: Observable<BankOption[]>;
  public transferDays: number[] = [];
  public showTransferDay = false;
  public transferDayLabel = 'Dia de Recebimento';
  public transferDayOptionPrefix = 'Dia';
  public transferDayErrorMessage = 'Informe um dia valido.';
  public descriptionCreditCard = 'Visa, Mastercard, Hipercard, Elo, American Express (Amex), Diners, Hiper, Cabal.';

  private readonly banksApiUrl = 'https://brasilapi.com.br/api/banks/v1';
  private readonly settingsRefreshDelayMs = 5000;
  private settingsRefreshTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;
  private onboardingRequestInFlight = false;
  private onboardingLoadingDialogRef?: MatDialogRef<OnboardingPagarmeComponent>;
  private onboardingDialogRef?: MatDialogRef<OnboardingPagarmeComponent>;

  constructor(
    private fb: FormBuilder,
    private organizationSettingsService: OrganizationSettingsService,
    private paymentSettingsService: PaymentSettingsService,
    private injector: Injector,
    private http: HttpClient,
    private dialogService: DialogService,
  ) {
    super(injector);

    this.bankDetailsForm = this.fb.group({
      bank_search: [null, Validators.required],
      holder_name: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(30), Validators.pattern(/^(?=.*\S).+$/)]],
      holder_type: [null, Validators.required],
      holder_document: [null, Validators.required],
      bank: [null, [Validators.required, Validators.pattern(/^\d{3}$/)]],
      bank_number: [null, [Validators.required, Validators.pattern(/^\d{3}$/)]],
      branch_number: [null, [Validators.required, Validators.pattern(/^\d{1,5}$/)]],
      branch_check_digit: ['', [Validators.pattern(/^\d?$/)]],
      account_number: ['', [Validators.required, Validators.pattern(/^\d{4,13}$/)]],
      account_check_digit: ['', [Validators.required, Validators.pattern(/^[0-9Xx]$/)]],
      type: [null, Validators.required],
      transfer_interval: ['daily', Validators.required],
      transfer_day: [null, [Validators.required, Validators.min(1), Validators.max(31)]],
      company_name: [null],
      trading_name: [null],
      annual_revenue: [null],
      corporation_type: [null],
      founding_date: [null],
      site_url: [null],
      company_phone_ddd: [null],
      company_phone_number: [null],
      company_street: [null],
      company_street_number: [null],
      company_complementary: [null],
      company_neighborhood: [null],
      company_city: [null],
      company_state: [null],
      company_zipcode: [null],
      company_reference_point: [null],
      partner_name: [null],
      partner_document_number: [null],
      partner_mother_name: [null],
      partner_birthdate: [null],
      partner_email: [null],
      partner_monthly_income: [null],
      partner_professional_occupation: [null],
      partner_street: [null],
      partner_street_number: [null],
      partner_complementary: [null],
      partner_neighborhood: [null],
      partner_city: [null],
      partner_state: [null],
      partner_zipcode: [null],
      partner_reference_point: [null],
      partner_phone_ddd: [null],
      partner_phone_number: [null],
    });

    this.paymentMethodsForm = this.fb.group({
      PIXActive: [false, Validators.required],
      CreditCardActive: [false, Validators.required],
      CreditCardDescription: [{ value: this.descriptionCreditCard, disabled: true }, Validators.required],
      PhysicalActive: [true, Validators.required],
    });

    this.commissionForm = this.fb.group({
      organization_percentage: [{ value: 90, disabled: true }, [Validators.required]],
    });

    this.configureBankFilter();
    this.watchDocumentType();
  }

  get organizationPercentage(): number {
    return 90;
  }

  get commissionTotalInvalid(): boolean {
    return this.commissionForm.invalid;
  }

  get paymentMethodsInvalid(): boolean {
    return this.paymentMethodsForm.invalid;
  }

  ngOnInit(): void {
    this.onTransferIntervalChanged();
    this.bankDetailsForm.get('transfer_interval')?.valueChanges.subscribe(() => this.onTransferIntervalChanged());
    this.loadBanks();
    this.getSettings(() => this.refreshSettingsAfterSave());
    this.loadCommissionSettings();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.closeOnboardingLoadingModal();
    this.onboardingDialogRef?.close();

    if (this.settingsRefreshTimer) {
      clearTimeout(this.settingsRefreshTimer);
    }
  }

  get bankSearch() {
    return this.bankDetailsForm.get('bank_search');
  }

  get accountNumber() {
    return this.bankDetailsForm.get('account_number');
  }

  get accountDigit() {
    return this.bankDetailsForm.get('account_check_digit');
  }

  get documentControl() {
    return this.bankDetailsForm.get('holder_document');
  }

  get holderName() {
    return this.bankDetailsForm.get('holder_name');
  }

  get currentRecipientId(): string {
    return this.paymentSettings?.recipient_info?.recipient_id || this.paymentSettings?.recipient_info?.['id'] || '';
  }

  get canOpenOnboarding(): boolean {
    return !!this.currentRecipientId && this.recipientStatus.code !== 'active' && (this.hasValidOnboardingLink() || this.canCreateOnboardingLink());
  }

  get recipientStatus(): RecipientVisualStatus {
    return buildRecipientVisualStatus(this.paymentSettings);
  }

  get isCompanySelected(): boolean {
    return this.bankDetailsForm.get('holder_type')?.value === 'company';
  }

  private configureBankFilter(): void {
    this.filteredBanks$ = this.bankDetailsForm.get('bank_search')!.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterBanks(value)),
    );
  }

  public onTransferIntervalChanged(): void {
    const interval = this.bankDetailsForm.get('transfer_interval')?.value;
    const transferDayControl = this.bankDetailsForm.get('transfer_day');

    if (!transferDayControl) {
      return;
    }

    if (interval === 'daily') {
      this.showTransferDay = false;
      this.transferDays = [];
      transferDayControl.setValue(0);
      transferDayControl.clearValidators();
      transferDayControl.updateValueAndValidity();
      return;
    }

    if (interval === 'weekly') {
      this.showTransferDay = true;
      this.transferDayLabel = 'Dia da Semana para Recebimento';
      this.transferDayOptionPrefix = 'Dia';
      this.transferDayErrorMessage = 'Informe um dia entre 1 e 5.';
      this.transferDays = [1, 2, 3, 4, 5];
      transferDayControl.setValidators([Validators.required, Validators.min(1), Validators.max(5)]);

      if (!this.transferDays.includes(Number(transferDayControl.value))) {
        transferDayControl.setValue(1);
      }

      transferDayControl.updateValueAndValidity();
      return;
    }

    if (interval === 'monthly') {
      this.showTransferDay = true;
      this.transferDayLabel = 'Dia do Mes para Recebimento';
      this.transferDayOptionPrefix = 'Dia';
      this.transferDayErrorMessage = 'Informe um dia entre 1 e 31.';
      this.transferDays = Array.from({ length: 31 }, (_, index) => index + 1);
      transferDayControl.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);

      if (!this.transferDays.includes(Number(transferDayControl.value))) {
        transferDayControl.setValue(1);
      }

      transferDayControl.updateValueAndValidity();
    }
  }

  private loadBanks(): void {
    this.http
      .get<BankOption[]>(this.banksApiUrl)
      .pipe(
        map((banks) =>
          banks
            .filter((bank) => bank.code !== null && bank.code !== undefined)
            .sort((a, b) => (a.name || a.fullName || '').localeCompare(b.name || b.fullName || '')),
        ),
        catchError((err) => {
          console.error('Erro ao carregar bancos:', err);
          this.showErrorMessage = true;
          return of([]);
        }),
      )
      .subscribe((banks) => {
        this.banks = banks;

        const currentBankCode = this.bankDetailsForm.get('bank_number')?.value || this.bankDetailsForm.get('bank')?.value;

        if (currentBankCode) {
          this.setBankSearchByCode(currentBankCode);
        }

        this.bankDetailsForm.get('bank_search')?.updateValueAndValidity();
      });
  }

  private watchDocumentType(): void {
    this.bankDetailsForm.get('holder_type')?.valueChanges.subscribe((holderType) => {
      const control = this.bankDetailsForm.get('holder_document');

      control?.clearValidators();
      this.documentMask = null;
      this.documentPlaceholder = 'Selecione o tipo de pessoa';

      if (holderType === 'individual') {
        control?.setValidators([Validators.required, cpfValidator]);
        this.documentMask = '000.000.000-00';
        this.documentPlaceholder = 'Digite o CPF';
      } else if (holderType === 'company') {
        control?.setValidators([Validators.required, cnpjValidator]);
        this.documentMask = '00.000.000/0000-00';
        this.documentPlaceholder = 'Digite o CNPJ';
      } else {
        control?.setValidators([Validators.required]);
      }

      control?.updateValueAndValidity();
      this.configureCompanyValidators(holderType === 'company');
    });
  }

  private configureCompanyValidators(isCompany: boolean): void {
    const requiredFields = [
      'company_name',
      'trading_name',
      'annual_revenue',
      'corporation_type',
      'founding_date',
      'site_url',
      'company_phone_ddd',
      'company_phone_number',
      'company_street',
      'company_street_number',
      'company_complementary',
      'company_neighborhood',
      'company_city',
      'company_state',
      'company_zipcode',
      'company_reference_point',
      'partner_name',
      'partner_document_number',
      'partner_birthdate',
      'partner_email',
      'partner_monthly_income',
      'partner_professional_occupation',
      'partner_street',
      'partner_street_number',
      'partner_complementary',
      'partner_neighborhood',
      'partner_city',
      'partner_state',
      'partner_zipcode',
      'partner_reference_point',
      'partner_phone_ddd',
      'partner_phone_number',
    ];

    requiredFields.forEach((field) => {
      const validators: ValidatorFn[] = [Validators.required];

      if (field === 'partner_document_number') {
        validators.push(cpfValidator);
      }

      if (field.endsWith('_ddd')) {
        validators.push(Validators.pattern(/^\d{2}$/));
      }

      if (field.endsWith('_zipcode')) {
        validators.push(Validators.pattern(/^\d{8}$/));
      }

      if (field.endsWith('_number') && field.includes('phone')) {
        validators.push(Validators.pattern(/^\d{8,11}$/));
      }

      if (field.endsWith('_state')) {
        validators.push(Validators.pattern(/^[A-Za-z]{2}$/));
      }

      this.bankDetailsForm.get(field)?.setValidators(isCompany ? validators : []);
      this.bankDetailsForm.get(field)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  public displayBank = (bank: BankOption | string | null): string => {
    if (!bank) {
      return '';
    }

    if (typeof bank === 'string') {
      return bank;
    }

    return this.getBankLabel(bank);
  };

  public onBankSelected(event: MatAutocompleteSelectedEvent): void {
    const bank = event.option.value as BankOption;
    this.selectBank(bank);
  }

  public onBankInputChanged(): void {
    this.bankDetailsForm.patchValue({ bank: null, bank_number: null }, { emitEvent: false });
    this.bankDetailsForm.get('bank')?.markAsTouched();
    this.bankDetailsForm.get('bank_number')?.markAsTouched();
    this.bankDetailsForm.get('bank')?.updateValueAndValidity();
    this.bankDetailsForm.get('bank_number')?.updateValueAndValidity();
  }

  public trySelectTypedBank(): void {
    setTimeout(() => {
      const value = this.bankDetailsForm.get('bank_search')?.value;

      if (!value || typeof value !== 'string') {
        return;
      }

      const bank = this.findExactBank(value);

      if (bank) {
        this.selectBank(bank);
      }
    }, 200);
  }

  private selectBank(bank: BankOption): void {
    const bankCode = this.formatBankCode(bank.code);

    this.bankDetailsForm.patchValue({
      bank_search: bank,
      bank: bankCode,
      bank_number: bankCode,
    });

    this.bankDetailsForm.get('bank_search')?.markAsTouched();
    this.bankDetailsForm.get('bank')?.markAsTouched();
    this.bankDetailsForm.get('bank_number')?.markAsTouched();
    this.bankDetailsForm.get('bank')?.updateValueAndValidity();
    this.bankDetailsForm.get('bank_number')?.updateValueAndValidity();
  }

  private setBankSearchByCode(code: string | number): void {
    const bank = this.findBankByCode(code);
    const bankCode = this.formatBankCode(code);

    this.bankDetailsForm.patchValue(
      {
        bank_search: bank || bankCode,
        bank: bank ? this.formatBankCode(bank.code) : bankCode,
        bank_number: bank ? this.formatBankCode(bank.code) : bankCode,
      },
      { emitEvent: false },
    );
  }

  private filterBanks(value: BankOption | string | null): BankOption[] {
    const search = typeof value === 'string' ? value : value ? this.getBankLabel(value) : '';
    const term = this.normalizeText(search);
    const digits = search.replace(/\D/g, '');

    if (!term && !digits) {
      return this.banks.slice(0, 30);
    }

    return this.banks
      .filter((bank) => {
        const code = this.formatBankCode(bank.code);
        const bankText = this.normalizeText(`${bank.name || ''} ${bank.fullName || ''} ${code}`);
        return (!!term && bankText.includes(term)) || (!!digits && code.includes(digits));
      })
      .slice(0, 50);
  }

  private findExactBank(value: string): BankOption | undefined {
    const digits = value.replace(/\D/g, '');
    const term = this.normalizeText(value);

    if (digits) {
      const bankByCode = this.findBankByCode(digits);

      if (bankByCode) {
        return bankByCode;
      }
    }

    return this.banks.find((bank) => {
      const name = this.normalizeText(bank.name || '');
      const fullName = this.normalizeText(bank.fullName || '');
      const label = this.normalizeText(this.getBankLabel(bank));
      return name === term || fullName === term || label === term;
    });
  }

  private findBankByCode(code: string | number): BankOption | undefined {
    const normalizedCode = this.normalizeBankCode(code);
    return this.banks.find((bank) => this.normalizeBankCode(bank.code) === normalizedCode);
  }

  public formatBankCode(code: string | number | null | undefined): string {
    return String(code ?? '')
      .replace(/\D/g, '')
      .padStart(3, '0');
  }

  private normalizeBankCode(code: string | number | null | undefined): string {
    const onlyDigits = String(code ?? '').replace(/\D/g, '');
    const withoutLeadingZeros = onlyDigits.replace(/^0+/, '');
    return withoutLeadingZeros || '0';
  }

  private getBankLabel(bank: BankOption): string {
    return `${this.formatBankCode(bank.code)} - ${bank.fullName || bank.name}`;
  }

  private normalizeText(value: string): string {
    return String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private onlyDigits(value: string | number | null | undefined): string {
    return String(value ?? '').replace(/\D/g, '');
  }

  private normalizeAccountDigit(value: string | null | undefined): string {
    const normalized = String(value ?? '')
      .trim()
      .toUpperCase();

    return normalized || '0';
  }

  public onBranchCheckDigitBlur(): void {
    const control = this.bankDetailsForm.get('branch_check_digit');

    if (!control) {
      return;
    }

    if (control.value === null || control.value === undefined || String(control.value).trim() === '') {
      control.setValue('0');
    }
  }

  private splitAccountNumber(accountNumber?: string | null): { number: string | null; digit: string | null } {
    if (!accountNumber) {
      return { number: null, digit: null };
    }

    const [number, digit] = accountNumber.split('-');
    return { number: number || null, digit: digit || null };
  }

  private getBankAccountPayload(): NonNullable<PaymentSettings['bank_account']> {
    const raw = this.bankDetailsForm.getRawValue();
    const bankCode = this.formatBankCode(raw.bank_number || raw.bank);

    const payload: NonNullable<PaymentSettings['bank_account']> = {
      holder_name: String(raw.holder_name).trim(),
      holder_type: raw.holder_type,
      holder_document: this.onlyDigits(raw.holder_document),
      bank: bankCode,
      bank_number: bankCode,
      branch_number: this.onlyDigits(raw.branch_number),
      branch_check_digit: this.normalizeAccountDigit(raw.branch_check_digit),
      account_number: this.onlyDigits(raw.account_number),
      account_check_digit: this.normalizeAccountDigit(raw.account_check_digit),
      type: raw.type,
    };

    return payload;
  }

  private getRegisterInformationPayload(): PaymentSettings['register_information'] | undefined {
    if (!this.isCompanySelected) {
      return undefined;
    }

    const raw = this.bankDetailsForm.getRawValue();

    return {
      company_name: String(raw.company_name).trim(),
      trading_name: String(raw.trading_name).trim(),
      annual_revenue: this.onlyDigits(raw.annual_revenue),
      corporation_type: String(raw.corporation_type).trim(),
      founding_date: raw.founding_date,
      site_url: String(raw.site_url).trim(),
      phone: {
        ddd: this.onlyDigits(raw.company_phone_ddd),
        number: this.onlyDigits(raw.company_phone_number),
        type: 'mobile',
      },
      main_address: {
        street: String(raw.company_street).trim(),
        street_number: String(raw.company_street_number).trim(),
        complementary: String(raw.company_complementary).trim(),
        neighborhood: String(raw.company_neighborhood).trim(),
        city: String(raw.company_city).trim(),
        state: String(raw.company_state).trim().toUpperCase(),
        zipcode: this.onlyDigits(raw.company_zipcode),
        reference_point: String(raw.company_reference_point).trim(),
      },
      managing_partner: {
        name: String(raw.partner_name).trim(),
        document_number: this.onlyDigits(raw.partner_document_number),
        mother_name: String(raw.partner_mother_name || '').trim(),
        birthdate: raw.partner_birthdate,
        email: String(raw.partner_email).trim(),
        monthly_income: this.onlyDigits(raw.partner_monthly_income),
        professional_occupation: String(raw.partner_professional_occupation).trim(),
        address: {
          street: String(raw.partner_street).trim(),
          street_number: String(raw.partner_street_number).trim(),
          complementary: String(raw.partner_complementary).trim(),
          neighborhood: String(raw.partner_neighborhood).trim(),
          city: String(raw.partner_city).trim(),
          state: String(raw.partner_state).trim().toUpperCase(),
          zipcode: this.onlyDigits(raw.partner_zipcode),
          reference_point: String(raw.partner_reference_point).trim(),
        },
        phone: {
          ddd: this.onlyDigits(raw.partner_phone_ddd),
          number: this.onlyDigits(raw.partner_phone_number),
          type: 'mobile',
        },
      },
    };
  }

  private getTransferSettingsPayload(): NonNullable<PaymentSettings['transfer_settings']> {
    const raw = this.bankDetailsForm.getRawValue();

    return {
      transfer_interval: raw.transfer_interval || 'daily',
      transfer_day: String(raw.transfer_day || '0'),
    };
  }

  public getSettings(afterLoad?: () => void): void {
    this.organizationSettingsService.findByOrganization(this.getUserLogged().id).subscribe({
      next: (settings: PaymentSettings[]) => {
        const paymentSettings = this.findBankSettings(settings);

        if (!paymentSettings) {
          if (!this.paymentSettings) {
            this.mode = 'CREATE';
          }

          afterLoad?.();
          return;
        }

        this.paymentSettings = this.mergePaymentSettings(paymentSettings);
        this.mode = 'UPDATE';

        const bankAccount = this.paymentSettings.bank_account;
        const transferSettings = this.paymentSettings.transfer_settings;
        const bankCode = bankAccount?.bank_number || bankAccount?.bank;
        const accountParts = this.splitAccountNumber(bankAccount?.account_number);

        this.bankDetailsForm.patchValue({
          holder_name: bankAccount?.holder_name,
          holder_type: bankAccount?.holder_type,
          holder_document: bankAccount?.holder_document,
          bank: bankCode ? this.formatBankCode(bankCode) : null,
          bank_number: bankCode ? this.formatBankCode(bankCode) : null,
          branch_number: bankAccount?.branch_number,
          branch_check_digit: bankAccount?.branch_check_digit,
          account_number: accountParts.digit ? accountParts.number : bankAccount?.account_number,
          account_check_digit: bankAccount?.account_check_digit || accountParts.digit,
          type: bankAccount?.type,
          transfer_interval: transferSettings?.transfer_interval || 'daily',
          transfer_day: transferSettings?.transfer_day ?? 1,
          company_name: this.paymentSettings.register_information?.company_name,
          trading_name: this.paymentSettings.register_information?.trading_name,
          annual_revenue: this.paymentSettings.register_information?.annual_revenue,
          corporation_type: this.paymentSettings.register_information?.corporation_type,
          founding_date: this.paymentSettings.register_information?.founding_date,
          site_url: this.paymentSettings.register_information?.site_url,
          company_phone_ddd: this.paymentSettings.register_information?.phone?.ddd,
          company_phone_number: this.paymentSettings.register_information?.phone?.number,
          company_street: this.paymentSettings.register_information?.main_address?.street,
          company_street_number: this.paymentSettings.register_information?.main_address?.street_number,
          company_complementary: this.paymentSettings.register_information?.main_address?.complementary,
          company_neighborhood: this.paymentSettings.register_information?.main_address?.neighborhood,
          company_city: this.paymentSettings.register_information?.main_address?.city,
          company_state: this.paymentSettings.register_information?.main_address?.state,
          company_zipcode: this.paymentSettings.register_information?.main_address?.zipcode,
          company_reference_point: this.paymentSettings.register_information?.main_address?.reference_point,
          partner_name: this.paymentSettings.register_information?.managing_partner?.name,
          partner_document_number: this.paymentSettings.register_information?.managing_partner?.document_number,
          partner_mother_name: this.paymentSettings.register_information?.managing_partner?.mother_name,
          partner_birthdate: this.paymentSettings.register_information?.managing_partner?.birthdate,
          partner_email: this.paymentSettings.register_information?.managing_partner?.email,
          partner_monthly_income: this.paymentSettings.register_information?.managing_partner?.monthly_income,
          partner_professional_occupation: this.paymentSettings.register_information?.managing_partner?.professional_occupation,
          partner_street: this.paymentSettings.register_information?.managing_partner?.address?.street,
          partner_street_number: this.paymentSettings.register_information?.managing_partner?.address?.street_number,
          partner_complementary: this.paymentSettings.register_information?.managing_partner?.address?.complementary,
          partner_neighborhood: this.paymentSettings.register_information?.managing_partner?.address?.neighborhood,
          partner_city: this.paymentSettings.register_information?.managing_partner?.address?.city,
          partner_state: this.paymentSettings.register_information?.managing_partner?.address?.state,
          partner_zipcode: this.paymentSettings.register_information?.managing_partner?.address?.zipcode,
          partner_reference_point: this.paymentSettings.register_information?.managing_partner?.address?.reference_point,
          partner_phone_ddd: this.paymentSettings.register_information?.managing_partner?.phone?.ddd,
          partner_phone_number: this.paymentSettings.register_information?.managing_partner?.phone?.number,
        });

        if (bankCode) {
          this.setBankSearchByCode(bankCode);
        }

        afterLoad?.();
      },
      error: () => {
        if (!this.paymentSettings) {
          this.mode = 'CREATE';
        }

        afterLoad?.();
      },
    });
  }

  public createOrUpdateSettings(): void {
    if (this.bankDetailsForm.invalid) {
      this.bankDetailsForm.markAllAsTouched();
      return;
    }

    if (this.mode === 'CREATE') {
      this.createSettings();
      return;
    }

    this.updateSettings();
  }

  public saveCommissionSettings(): void {
    if (this.commissionTotalInvalid) {
      this.commissionForm.markAllAsTouched();
      return;
    }

    const payload: CommissionSavePayload = this.getPaymentSettingsPayload();
    this.loading = true;
    const request = this.commissionPaymentSettings?.payment_settings_id
      ? this.paymentSettingsService.update(payload as PaymentSettings, this.commissionPaymentSettings.payment_settings_id)
      : this.paymentSettingsService.create(payload as PaymentSettings);

    request.subscribe({
      next: (savedSettings: PaymentSettings) => {
        this.commissionPaymentSettings = savedSettings;
        this.showSuccessMessage = true;
        this.loading = false;
      },
      error: (err: any) => {
        this.showErrorMessage = true;
        this.loading = false;
        this.messageError = getHttpErrorMessage(err, 'Não foi possível salvar a comissão. Tente novamente.');
        console.error(err);
      },
    });
  }

  private loadCommissionSettings(): void {
    this.paymentSettingsService.findByOrganization(this.getUserLogged().id).subscribe({
      next: (settings: PaymentSettings) => {
        this.commissionPaymentSettings = settings;
        this.commissionForm.patchValue({
          organization_percentage: 90,
        });
        this.populatePaymentMethodsForm(settings.payment_methods || []);
      },
      error: () => {
        this.commissionForm.patchValue({ organization_percentage: 90 });
        this.populatePaymentMethodsForm([]);
      },
    });
  }

  private findBankSettings(settings: PaymentSettings[]): PaymentSettings | undefined {
    return settings.find((setting) => !!this.getRecipientId(setting)) || settings.find((setting) => !!setting?.bank_account) || settings[0];
  }

  public savePaymentMethods(): void {
    if (this.paymentMethodsInvalid) {
      this.paymentMethodsForm.markAllAsTouched();
      return;
    }

    const payload = this.getPaymentSettingsPayload({
      payment_methods: this.getPaymentMethodsPayload(),
    });

    this.loading = true;
    const request = this.commissionPaymentSettings?.payment_settings_id
      ? this.paymentSettingsService.update(payload as PaymentSettings, this.commissionPaymentSettings.payment_settings_id)
      : this.paymentSettingsService.create(payload as PaymentSettings);

    request.subscribe({
      next: (savedSettings: PaymentSettings) => {
        this.commissionPaymentSettings = savedSettings;
        this.populatePaymentMethodsForm(savedSettings.payment_methods || payload.payment_methods || []);
        this.showSuccessMessage = true;
        this.loading = false;
      },
      error: (err: any) => {
        this.showErrorMessage = true;
        this.loading = false;
        this.messageError = getHttpErrorMessage(err, 'Não foi possível salvar as formas de pagamento. Tente novamente.');
        console.error(err);
      },
    });
  }

  private getPaymentMethodsPayload(): PaymentSettings['payment_methods'] {
    return [
      {
        name: 'PIX',
        active: this.paymentMethodsForm.value.PIXActive,
        description: 'PIX',
      },
      {
        name: 'CREDIT_CARD',
        active: this.paymentMethodsForm.value.CreditCardActive,
        description: this.paymentMethodsForm.getRawValue().CreditCardDescription || this.descriptionCreditCard,
      },
      {
        name: 'PHYSICAL',
        active: this.paymentMethodsForm.value.PhysicalActive,
        description: 'Presencial',
      },
    ];
  }

  private populatePaymentMethodsForm(paymentMethods: PaymentSettings['payment_methods']): void {
    const methods = paymentMethods || [];
    const pixMethod = methods.find((method) => method.name === 'PIX');
    const creditCardMethod = methods.find((method) => method.name === 'CREDIT_CARD');
    const physicalMethod = methods.find((method) => method.name === 'PHYSICAL');
    this.paymentMethodsForm.patchValue({
      PIXActive: pixMethod ? pixMethod.active : false,
      CreditCardActive: creditCardMethod ? creditCardMethod.active : false,
      CreditCardDescription: creditCardMethod?.description || this.descriptionCreditCard,
      PhysicalActive: physicalMethod ? physicalMethod.active : true,
    });
  }

  private getPaymentSettingsPayload(overrides: Partial<PaymentSettings> = {}): PaymentSettings {
    return {
      ...this.commissionPaymentSettings,
      organization_id: this.getUserLogged().id,
      organization_percentage: 90,
      payment_methods: this.commissionPaymentSettings?.payment_methods || this.getPaymentMethodsPayload(),
      ...overrides,
    };
  }

  private createSettings(): void {
    const newPaymentSettings: PaymentSettings = {
      organization_id: this.getUserLogged().id,
      payment_methods: [],
      bank_account: this.getBankAccountPayload(),
      register_information: this.getRegisterInformationPayload(),
      transfer_settings: this.getTransferSettingsPayload(),
    };

    this.loading = true;

    this.organizationSettingsService.createNew(newPaymentSettings).subscribe({
      next: (paymentSettings: PaymentSettings) => {
        this.showSuccessMessage = true;
        this.paymentSettings = this.mergePaymentSettings(paymentSettings);
        this.mode = 'UPDATE';
        this.loading = false;
        this.startOnboardingFlow();
        this.refreshSettingsAfterSave();
      },
      error: (err: any) => {
        this.showErrorMessage = true;
        this.loading = false;
        this.messageError = getHttpErrorMessage(err, 'Não foi possível salvar os dados bancários. Confira os dados e tente novamente.');
        console.error(err);
      },
    });
  }

  private updateSettings(): void {
    if (!this.paymentSettings?.payment_settings_id) {
      this.showErrorMessage = true;
      return;
    }

    const updatedPaymentSettings: PaymentSettings = {
      ...this.paymentSettings,
      bank_account: this.getBankAccountPayload(),
      register_information: this.getRegisterInformationPayload(),
      transfer_settings: this.getTransferSettingsPayload(),
    };

    this.loading = true;

    this.organizationSettingsService.updateNew(updatedPaymentSettings, this.paymentSettings.payment_settings_id).subscribe({
      next: (paymentSettings: PaymentSettings) => {
        this.showSuccessMessage = true;
        this.paymentSettings = this.mergePaymentSettings(paymentSettings);
        this.loading = false;
        this.startOnboardingFlow();
        this.refreshSettingsAfterSave();
      },
      error: (err: any) => {
        this.showErrorMessage = true;
        this.loading = false;
        this.messageError = getHttpErrorMessage(err, 'Não foi possível atualizar os dados bancários. Confira os dados e tente novamente.');
        console.error(err);
      },
    });
  }

  public openOrCreateOnboarding(): void {
    const recipientId = this.currentRecipientId;
    const onboardQrCode = this.paymentSettings?.onboard_qrcode;

    if (!recipientId) {
      this.messageError = 'Salve os dados bancarios antes de abrir o cadastro Pagar.me.';
      this.showErrorMessage = true;
      return;
    }

    if (this.recipientStatus.code === 'active') {
      return;
    }

    if (this.hasValidOnboardingLink()) {
      this.openOnboardingModal(onboardQrCode);
      return;
    }

    if (!this.canCreateOnboardingLink()) {
      this.messageError = 'Cadastro Pagar.me ainda nao permite gerar QRCode. Verifique o status do recebedor e os dados cadastrais.';
      this.showErrorMessage = true;
      return;
    }

    if (!onboardQrCode?.expires_at || this.isExpired(onboardQrCode.expires_at)) {
      this.createOnboarding(this.paymentSettings.organization_id || this.getUserLogged().id, recipientId, this.paymentSettings.payment_settings_id);
      return;
    }
  }

  private canCreateOnboardingLink(): boolean {
    const recipientInfo = this.paymentSettings?.recipient_info || {};
    const kycDetails = recipientInfo['kyc_details'] || {};

    return (
      String(recipientInfo['status'] || '').toLowerCase() === 'affiliation' &&
      String(kycDetails['status'] || '').toLowerCase() === 'partially_denied' &&
      String(kycDetails['status_reason'] || '').toLowerCase() === 'additional_documents_required'
    );
  }

  get onboardingButtonLabel(): string {
    if (!this.currentRecipientId || this.canOpenOnboarding) {
      return 'Abrir cadastro Pagar.me';
    }

    return 'Aguardando liberacao da Pagar.me';
  }

  private hasValidOnboardingLink(): boolean {
    const onboarding = this.paymentSettings?.onboard_qrcode;
    return !!onboarding?.url && !!onboarding.expires_at && !this.isExpired(onboarding.expires_at);
  }

  private createOnboarding(organizationId: string, recipientId: string, paymentSettingsId?: string): void {
    if (this.onboardingRequestInFlight) {
      return;
    }

    this.onboardingRequestInFlight = true;
    this.loading = true;
    this.openOnboardingLoadingModal();

    this.organizationSettingsService.createOnboarding(organizationId, recipientId, paymentSettingsId).subscribe({
      next: (onboarding: PaymentSettings['onboard_qrcode']) => {
        this.paymentSettings = {
          ...this.paymentSettings,
          onboard_qrcode: onboarding,
        };
        this.onboardingRequestInFlight = false;
        this.loading = false;
        this.closeOnboardingLoadingModal();
        this.openOnboardingModal(onboarding);
      },
      error: (err: any) => {
        this.onboardingRequestInFlight = false;
        this.closeOnboardingLoadingModal();
        this.messageError = this.getRequestErrorMessage(err);
        this.showErrorMessage = true;
        this.loading = false;
        console.error(err);
      },
    });
  }

  private startOnboardingFlow(): void {
    const recipientId = this.currentRecipientId;

    if (!recipientId || this.recipientStatus.code === 'active') {
      return;
    }

    if (this.hasValidOnboardingLink()) {
      this.openOnboardingModal(this.paymentSettings?.onboard_qrcode);
      return;
    }

    this.openOnboardingLoadingModal();
    if (this.canCreateOnboardingLink()) {
      this.createOnboarding(this.paymentSettings.organization_id || this.getUserLogged().id, recipientId, this.paymentSettings.payment_settings_id);
    }
  }

  private openOnboardingLoadingModal(): void {
    if (!this.onboardingLoadingDialogRef) {
      this.onboardingLoadingDialogRef = this.dialogService.openMediumComponentDialogRef(OnboardingPagarmeComponent, { loading: true });
    }
  }

  private closeOnboardingLoadingModal(): void {
    this.onboardingLoadingDialogRef?.close();
    this.onboardingLoadingDialogRef = undefined;
  }

  private openOnboardingWhenAvailable(): void {
    if (this.onboardingLoadingDialogRef && this.hasValidOnboardingLink()) {
      const onboarding = this.paymentSettings?.onboard_qrcode;
      this.closeOnboardingLoadingModal();
      this.openOnboardingModal(onboarding);
    }
  }

  private openOnboardingModal(onboarding: PaymentSettings['onboard_qrcode']): void {
    if (!onboarding) {
      return;
    }

    this.onboardingDialogRef = this.dialogService.openMediumComponentDialogRef(OnboardingPagarmeComponent, {
      param: onboarding,
    });
    this.onboardingDialogRef.afterClosed().subscribe(() => {
      this.onboardingDialogRef = undefined;
    });
  }

  private handleOnboardingStatus(): void {
    const recipient = this.paymentSettings?.recipient_info || {};
    const kycDetails = recipient['kyc_details'] || {};
    const kycStatus = String(kycDetails['status'] || recipient['kyc_status'] || '')
      .trim()
      .toLowerCase();
    const kycStatusReason = String(kycDetails['status_reason'] || '')
      .trim()
      .toLowerCase();
    const submitted =
      kycStatus === 'pending' && ['in_analysis', 'answered_waiting_analysis', 'waiting_manual_risk_analysis'].includes(kycStatusReason);
    const active = this.recipientStatus.code === 'active';

    if ((submitted || active) && (this.onboardingDialogRef || this.onboardingLoadingDialogRef)) {
      this.onboardingDialogRef?.close();
      this.closeOnboardingLoadingModal();
      this.messageSuccess = active
        ? 'Dados enviados com sucesso. O cadastro foi aprovado pela Pagar.me e está ativo.'
        : 'Dados enviados com sucesso. O cadastro está em análise pela Pagar.me.';
      this.showSuccessMessage = true;
    }
  }

  private isExpired(expiresAt: string): boolean {
    if (!expiresAt) {
      return true;
    }

    const expirationDate = new Date(expiresAt);

    if (Number.isNaN(expirationDate.getTime())) {
      return true;
    }

    return Date.now() >= expirationDate.getTime();
  }

  private getRequestErrorMessage(error: any): string {
    const responseMessage = error?.error?.message;

    if (typeof responseMessage === 'string') {
      return responseMessage;
    }

    if (typeof responseMessage?.message === 'string') {
      return responseMessage.message;
    }

    if (Array.isArray(responseMessage?.errors) && responseMessage.errors.length > 0) {
      const firstError = responseMessage.errors[0];

      if (typeof firstError?.message === 'string') {
        return firstError.message;
      }
    }

    return error?.message || 'Ocorreu uma falha no servidor. Tente novamente mais tarde.';
  }

  private getRecipientId(settings?: PaymentSettings): string {
    return settings?.recipient_info?.recipient_id || settings?.recipient_info?.['id'] || '';
  }

  private mergePaymentSettings(nextSettings: PaymentSettings): PaymentSettings {
    if (!this.paymentSettings) {
      return nextSettings;
    }

    return {
      ...nextSettings,
      recipient_info: this.getRecipientId(nextSettings) ? nextSettings.recipient_info : this.paymentSettings.recipient_info,
      onboard_qrcode: nextSettings.onboard_qrcode?.url ? nextSettings.onboard_qrcode : this.paymentSettings.onboard_qrcode,
    };
  }

  private refreshSettingsAfterSave(): void {
    if (this.destroyed || !this.shouldRefreshSettings()) {
      return;
    }

    if (this.settingsRefreshTimer) {
      clearTimeout(this.settingsRefreshTimer);
    }

    this.settingsRefreshTimer = setTimeout(() => {
      this.getSettings(() => {
        this.handleOnboardingStatus();
        this.openOnboardingWhenAvailable();
        this.refreshSettingsAfterSave();
      });
    }, this.settingsRefreshDelayMs);
  }

  private shouldRefreshSettings(): boolean {
    return (
      !!this.onboardingDialogRef ||
      (!!this.currentRecipientId &&
        this.recipientStatus.code !== 'active' &&
        this.recipientStatus.code !== 'inactive' &&
        !this.hasValidOnboardingLink() &&
        !this.canCreateOnboardingLink())
    );
  }
}
